import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Icon from '../Icon';
import Select from 'react-select';
import { translate } from '../../Services/translation';
import { getColor } from '../../styling/mixins/colors';
import { fontFamily } from '../../styling/mixins/typography';

type DropdownSelectorField = 'value' | 'label';

interface DropdownAttributes {
  selectorField: DropdownSelectorField;
  options: DropdownOption[];
  onChange?: any;
  selectedValue: string | null;
  placeholder?: string;
  isDisabled?: boolean;
  isBackOrder?: boolean;
  onButtonClick: () => void;
  menuPlacement?: string;
}

export interface DropdownOption {
  value?: string;
  label?: string;
  inStock: boolean;
  stockStatus?: number;
  articleNumber: string;
  isDisabled?: boolean;
}

const DropdownWrapper = styled.div`
  width: 100%;
  
  .react-select-container {
    font-family: ${fontFamily};
    cursor: pointer;
    margin-bottom: 1rem;
  }

  .react-select__control {
    border-radius: 5px;
    padding: 0.6rem;
    margin: 0;
    box-shadow: none;
    border-color: ${getColor('gray', 400)};

    &:hover,
    &:focus {
      border-color: ${getColor('gray', 500)};
    }
  }

  .react-select__indicator-separator {
    display: none;
  }

  .disabled {
    opacity: 0.5;
  }

  .option {
    border-radius: 1px solid grey;
    padding: 1.2rem 1rem;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid ${getColor('gray', 100)};

    &:hover {
      background-color: ${getColor('gray', 200)};
    }

    &:last-child {
      border-bottom: none;
    }

    .status {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.4rem;
      font-size: 1.3rem;
      cursor: pointer;

      span {
        display: flex;
        align-items: center;
        cursor: pointer;
      }

      .icon {
        font-size: 1.6rem;
        margin-right: 0.4rem;
        cursor: pointer;
      }
    }
  }
`;

const NewDropdown = (props: DropdownAttributes) => {
  var [ready, setReady] = useState(false);

  const handleButtonClick = () => {
    props.onButtonClick();
  };

  useEffect(() => {
    setReady(true);
  });

  var selectedValue = useMemo(() => {
    return (
      props.options.find(
        (x) => x[props.selectorField] == props.selectedValue
      ) ?? null
    );
  }, [props.selectedValue]);

  const FormatStockStatus = (stockStatus: number) => {
    if (stockStatus > 5) {
      return `5+`;
    }
    if (stockStatus == 5) {
      return `5`;
    } else {
      return `${stockStatus}`;
    }
  };

  const OptionLayout = (props) => {
    var { innerRef, innerProps } = props;
    const disabledClass = props.data.isDisabled ? 'disabled' : '';

    return (
      <div
        className={`option relative ${disabledClass}`}
        ref={innerRef}
        {...innerProps}
      >
        <span>{props.data.label}</span>
        <div className="status">
          {props.data.isDisabled == true ? (
            <span>{translate('product-listing.is-not-available')}</span>
          ) : (
            <></>
          )}
          {props.data.stockStatus < 1 &&
          props.data.isBackOrder == false &&
          props.data.isDisabled == false ? (
            <>
              <span className="mx-1 hover:opacity-100">
                <Icon name={'cross-circle'} />
                {translate('product-listing.is-not-in-stock')}{' '}
              </span>
              {props.onButtonClick && (
                // Remind me component
                <>
                  <button
                    onClick={handleButtonClick}
                    className="flex absolute items-center right-0 pr-2 top-0 bottom-0"
                  >
                    <>
                      <Icon name="mail" className="!w-5 !h-5" />
                      <span className="underline underline-offset-2 decoration-1">
                        {translate('out-of-stock-openmodal')}{' '}
                      </span>
                    </>
                  </button>
                  <span className="flex">
                    <>
                      <Icon name="mail" className="!w-5 !h-5 invisible " />
                      <span className="underline underline-offset-2 decoration-1 invisible">
                        {translate('out-of-stock-openmodal')}{' '}
                      </span>
                    </>
                  </span>
                </>
              )}
            </>
          ) : (
            <></>
          )}
          {props.data.stockStatus < 1 &&
          props.data.isBackOrder == true &&
          props.data.isDisabled == false ? (
            <span>
              <Icon name={'check-circle-orange'} />
              {translate('stock.isbackorder')}
            </span>
          ) : (
            <></>
          )}
          {props.data.stockStatus > 0 && props.data.isDisabled == false ? (
            <span>
              <Icon name={'check-circle'} />
              {FormatStockStatus(props.data.stockStatus)}{' '}
              {translate('product-listing.is-in-stock')}
            </span>
          ) : (
            <></>
          )}
        </div>
      </div>
    );
  };

  return (
    <DropdownWrapper>
      {ready == true ? (
        <Select
          placeholder={props.placeholder}
          components={{ Option: OptionLayout }}
          className="react-select-container"
          classNamePrefix="react-select"
          options={props.options}
          value={selectedValue}
          onChange={props.onChange}
          isSearchable={false}
          menuPlacement={props.menuPlacement}
        />
      ) : (
        <></>
      )}
    </DropdownWrapper>
  );
};

export default NewDropdown;
